import { Lock } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useBlocker, useSearchParams } from 'react-router-dom';
import {
  autoWithheld,
  currentSession,
  dropPercent,
  enabledSessions,
  isMilkDrop,
  milkingCowsOn,
  sessionAverage,
  type AutoWithheld,
} from '@dairy/domain';
import {
  addDays,
  FARM_TIME_ZONE,
  formatLiters,
  litersInputValue,
  localDateTimeInTimeZone,
  recordIds,
  tryParseLiters,
  type MilkRecordRecord,
  type MilkSession,
  type WithheldReason,
} from '@dairy/shared';
import { useIsOwner, useProfile, useSettings } from '@/auth/useSession';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { DateField } from '@/components/DateField';
import { LitersInput } from '@/components/inputs';
import { toast } from '@/components/toast';
import { Badge, Button, Dialog, Field, PageHeader, Segmented, Select, Textarea, EmptyState } from '@/components/ui';
import { isLocked, saveMany, type Change } from '@/db/repo';
import { useHerd, useToday } from '@/db/hooks';
import { useFormat } from '@/lib/format';
import { cn } from '@/lib/cn';
import { errorMessage } from '@/components/errors';
import { useMilkRecords } from './useMilk';

interface RowState {
  value: string;
  withheld: boolean;
  reason: WithheldReason | null;
  note: string;
}

const HIGH_LITERS = 25_000;

/** Screen 3 — Milk session entry (MR-1, MR-3, MR-5; S2-T03). */
export function MilkSessionPage() {
  const { t } = useTranslation();
  const f = useFormat();
  const s = useSettings();
  const profile = useProfile();
  const isOwner = useIsOwner();
  const today = useToday();
  const [params, setParams] = useSearchParams();
  const date = params.get('date') ?? today;
  const sessions = enabledSessions(s);
  const session = (params.get('session') as MilkSession | null) ?? currentSession(s, localDateTimeInTimeZone(FARM_TIME_ZONE));
  const herd = useHerd(date);
  const records = useMilkRecords(addDays(date, -8), date);
  const cows = useMemo(
    () => milkingCowsOn(herd.rows.map((r) => r.animal), herd.eventsByAnimal, date, s),
    [herd.rows, herd.eventsByAnimal, date, s],
  );
  const existing = useMemo(() => {
    const m = new Map<string, MilkRecordRecord>();
    for (const r of records ?? []) if (r.date === date && r.session === session) m.set(r.animalId, r);
    return m;
  }, [records, date, session]);

  const [rows, setRows] = useState<Record<string, RowState>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [dirty, setDirty] = useState(false);
  const [drops, setDrops] = useState<Record<string, number>>({});
  const [warn, setWarn] = useState<string[] | null>(null);
  const [noteFor, setNoteFor] = useState<{ ids: string[]; kind: 'zero' } | null>(null);
  const [withholdFor, setWithholdFor] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const inputs = useRef<Record<string, HTMLInputElement | null>>({});
  const initializedFor = useRef('');

  const autos = useMemo(() => {
    const m = new Map<string, AutoWithheld | null>();
    for (const c of cows) m.set(c.id, autoWithheld(c, herd.eventsByAnimal.get(c.id) ?? [], date, s));
    return m;
  }, [cows, herd.eventsByAnimal, date, s]);

  // Load saved values when the date/session (or the data) arrives; never overwrite the user's typing.
  useEffect(() => {
    const key = `${date}|${session}`;
    if (!records || herd.loading) return;
    if (initializedFor.current === key && dirty) return;
    const next: Record<string, RowState> = {};
    for (const c of cows) {
      const r = existing.get(c.id);
      const auto = autos.get(c.id) ?? null;
      next[c.id] = r
        ? { value: litersInputValue(r.ml), withheld: r.withheld, reason: (r.withheldReason as WithheldReason | null) ?? null, note: r.withheldNote ?? '' }
        : { value: '', withheld: !!auto, reason: auto?.reason ?? null, note: '' };
    }
    setRows(next);
    setErrors({});
    setDrops({});
    setDirty(false);
    initializedFor.current = key;
  }, [date, session, records, herd.loading, cows.length]);

  const blocker = useBlocker(({ currentLocation, nextLocation }) => dirty && currentLocation.pathname !== nextLocation.pathname);

  const setRow = (id: string, patch: Partial<RowState>) => {
    setRows((r) => ({ ...r, [id]: { ...r[id]!, ...patch } }));
    setDirty(true);
  };

  const parsed = cows.map((c) => {
    const row = rows[c.id];
    const p = row ? tryParseLiters(row.value) : { ok: true as const, ml: null };
    return { cow: c, row, p };
  });
  const totalMl = parsed.reduce((a, x) => a + (x.p.ok && x.p.ml ? x.p.ml : 0), 0);
  const recorded = parsed.filter((x) => x.p.ok && x.p.ml !== null).length;

  const goNext = (index: number) => {
    const next = cows[index + 1];
    if (next) inputs.current[next.id]?.focus();
    else inputs.current[cows[index]!.id]?.blur();
  };

  /** Validate, ask for soft confirmations, then save. */
  const attemptSave = (step: 'start' | 'afterWarn' | 'afterNote' = 'start') => {
    const errs: Record<string, string> = {};
    for (const x of parsed) if (!x.p.ok) errs[x.cow.id] = t('milk.invalidLiters');
    setErrors(errs);
    if (Object.keys(errs).length) return;
    if (step === 'start') {
      const warnings = parsed
        .filter((x) => x.p.ok && x.p.ml !== null && isChanged(x.cow.id))
        .filter((x) => {
          const ml = (x.p as { ml: number }).ml;
          const avg = sessionAverage(records ?? [], x.cow.id, session, date);
          return ml > HIGH_LITERS || (avg.count > 0 && ml > 2 * avg.avgMl);
        })
        .map((x) => x.cow.tag);
      if (warnings.length) {
        setWarn(warnings);
        return;
      }
    }
    if (step !== 'afterNote') {
      const zeros = parsed.filter((x) => x.p.ok && x.p.ml === 0 && isChanged(x.cow.id) && !rows[x.cow.id]?.note).map((x) => x.cow.id);
      if (zeros.length) {
        setNoteFor({ ids: zeros, kind: 'zero' });
        return;
      }
    }
    void doSave();
  };

  const isChanged = (id: string) => {
    const r = rows[id];
    const e = existing.get(id);
    if (!r) return false;
    const p = tryParseLiters(r.value);
    if (!p.ok) return true;
    if (!e) return p.ml !== null;
    return p.ml !== e.ml || r.withheld !== e.withheld || (r.reason ?? null) !== (e.withheldReason ?? null) || (r.note || null) !== (e.withheldNote ?? null);
  };

  const doSave = async () => {
    const changes: Change[] = [];
    for (const x of parsed) {
      if (!x.row || !x.p.ok || !isChanged(x.cow.id)) continue;
      const e = existing.get(x.cow.id);
      if (e && isLocked(e, profile, s)) continue;
      const id = recordIds.milkRecord(profile.tenantId, x.cow.id, date, session);
      if (x.p.ml === null) {
        if (e) changes.push({ entity: 'milkRecord', op: 'delete', id });
        continue;
      }
      changes.push({
        entity: 'milkRecord',
        op: 'upsert',
        record: {
          ...(e ?? {}),
          id,
          animalId: x.cow.id,
          date,
          session,
          ml: x.p.ml,
          withheld: x.row.withheld,
          withheldReason: x.row.withheld ? x.row.reason : null,
          withheldNote: x.row.note.trim() || null,
        },
      });
    }
    if (!changes.length) {
      toast(t('milk.nothingToSave'), 'info');
      return;
    }
    setSaving(true);
    try {
      const started = performance.now();
      await saveMany(changes);
      console.info(`[NF-2] local save of ${changes.length} milk records: ${(performance.now() - started).toFixed(0)} ms`);
      const d: Record<string, number> = {};
      for (const x of parsed) {
        if (!x.p.ok || x.p.ml === null) continue;
        const avg = sessionAverage(records ?? [], x.cow.id, session, date);
        if (isMilkDrop(x.p.ml, avg, s.milkDropThreshold)) d[x.cow.id] = dropPercent(x.p.ml, avg.avgMl);
      }
      setDrops(d);
      setDirty(false);
      toast(navigator.onLine ? t('common.saved') : t('common.savedOffline'));
    } catch (e) {
      toast(errorMessage(t, e), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pb-24">
      <PageHeader title={t('milk.sessionTitle')} />
      <div className="mb-4 space-y-3">
        <DateField value={date} max={today} onChange={(d) => setParams({ date: d, session })} />
        <Segmented
          value={session}
          onChange={(v) => setParams({ date, session: v })}
          options={sessions.map((x) => ({ value: x.session, label: `${f.session(x.session)} · ${x.time}` }))}
        />
      </div>

      {cows.length === 0 && !herd.loading ? <EmptyState>{t('milk.noMilkingCows')}</EmptyState> : null}

      <ul className="space-y-2">
        {cows.map((cow, i) => {
          const row = rows[cow.id];
          if (!row) return null;
          const e = existing.get(cow.id);
          const locked = !!e && isLocked(e, profile, s);
          const auto = autos.get(cow.id) ?? null;
          const avg = sessionAverage(records ?? [], cow.id, session, date);
          const autoLocked = !!auto && !isOwner;
          return (
            <li key={cow.id} className="rounded-2xl border border-stone-200 bg-white p-3" data-testid={`milk-row-${cow.tag}`}>
              <div className="flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-lg font-bold">
                    {cow.tag}
                    {cow.name ? <span className="ml-2 text-base font-normal text-stone-600">{cow.name}</span> : null}
                  </div>
                  <div className="text-sm text-stone-600">
                    {avg.count ? t('milk.avgHint', { liters: formatLiters(Math.round(avg.avgMl)) }) : t('milk.noAvg')}
                  </div>
                </div>
                <div className="w-28 shrink-0">
                  <LitersInput
                    ref={(el) => {
                      inputs.current[cow.id] = el;
                    }}
                    aria-label={t('milk.litersFor', { tag: cow.tag })}
                    data-testid={`liters-${cow.tag}`}
                    value={row.value}
                    disabled={locked}
                    aria-invalid={!!errors[cow.id]}
                    enterKeyHint={i === cows.length - 1 ? 'done' : 'next'}
                    onChange={(ev) => setRow(cow.id, { value: ev.target.value })}
                    onKeyDown={(ev) => {
                      if (ev.key === 'Enter') {
                        ev.preventDefault();
                        goNext(i);
                      }
                    }}
                    className="text-right text-lg"
                  />
                </div>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {locked ? (
                  <Badge tone="neutral">
                    <Lock className="h-3.5 w-3.5" /> {t('common.lockedAskOwner')}
                  </Badge>
                ) : null}
                <button
                  type="button"
                  disabled={locked || (autoLocked && row.withheld)}
                  onClick={() => {
                    if (row.withheld) {
                      if (auto && !isOwner) return;
                      setRow(cow.id, { withheld: false, reason: null });
                    } else if (auto) setRow(cow.id, { withheld: true, reason: auto.reason });
                    else setWithholdFor(cow.id);
                  }}
                  className={cn(
                    'min-h-10 rounded-full border px-3 text-sm font-medium disabled:opacity-80',
                    row.withheld ? 'border-amber-400 bg-amber-100 text-amber-950' : 'border-stone-300 text-stone-700',
                  )}
                  data-testid={`withheld-${cow.tag}`}
                >
                  {row.withheld
                    ? auto && row.reason === auto.reason
                      ? t(`milk.withheldUntil.${auto.reason}`, { date: f.short(auto.until) })
                      : t(`milk.withheldReason.${row.reason ?? 'OTHER'}`)
                    : t('milk.withhold')}
                  {autoLocked && row.withheld ? <Lock className="ml-1 inline h-3.5 w-3.5" /> : null}
                </button>
                {drops[cow.id] !== undefined ? <Badge tone="amber">{t('milk.dropBadge', { pct: drops[cow.id] })}</Badge> : null}
                {errors[cow.id] ? <span className="text-sm text-red-700">{errors[cow.id]}</span> : null}
                {row.note && !row.withheld ? <span className="text-sm text-stone-600">{row.note}</span> : null}
              </div>
            </li>
          );
        })}
      </ul>

      <p className="mt-4 text-center text-sm text-stone-600">
        {isOwner ? (
          <Link to="/animals" className="font-semibold text-brand-800">
            {t('milk.cowMissingOwner')}
          </Link>
        ) : (
          t('milk.cowMissingManager')
        )}
      </p>

      <div className="no-print safe-bottom fixed inset-x-0 bottom-16 z-20 border-t border-stone-200 bg-white/95 px-4 py-3 backdrop-blur lg:bottom-0 lg:left-60">
        <div className="mx-auto flex max-w-5xl items-center gap-3">
          <div className="flex-1 text-sm">
            <div className="text-lg font-bold tabular-nums" data-testid="session-total">
              {t('milk.footerTotal', { liters: f.liters(totalMl) })}
            </div>
            <div className="text-stone-600">{t('milk.footerCows', { count: recorded, total: cows.length })}</div>
          </div>
          <Button size="lg" onClick={() => attemptSave()} disabled={saving || cows.length === 0} data-testid="save-session">
            {t('common.save')}
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={!!warn}
        onOpenChange={(o) => !o && setWarn(null)}
        title={t('milk.highValuesTitle')}
        confirmLabel={t('common.saveAnyway')}
        onConfirm={() => {
          setWarn(null);
          attemptSave('afterWarn');
        }}
      >
        <p>{t('milk.highValuesBody', { tags: warn?.join(', ') })}</p>
      </ConfirmDialog>

      <ZeroNoteDialog
        open={!!noteFor}
        tags={(noteFor?.ids ?? []).map((id) => cows.find((c) => c.id === id)?.tag ?? '')}
        onCancel={() => setNoteFor(null)}
        onSubmit={(note) => {
          const ids = noteFor?.ids ?? [];
          setRows((r) => {
            const n = { ...r };
            for (const id of ids) n[id] = { ...n[id]!, note };
            return n;
          });
          setNoteFor(null);
          setTimeout(() => attemptSave('afterNote'), 0);
        }}
      />

      <WithholdDialog
        open={!!withholdFor}
        onCancel={() => setWithholdFor(null)}
        onSubmit={(reason, note) => {
          if (withholdFor) setRow(withholdFor, { withheld: true, reason, note });
          setWithholdFor(null);
        }}
      />

      <ConfirmDialog
        open={blocker.state === 'blocked'}
        onOpenChange={(o) => !o && blocker.reset?.()}
        title={t('milk.leaveTitle')}
        confirmLabel={t('milk.leaveConfirm')}
        danger
        onConfirm={() => blocker.proceed?.()}
      >
        <p>{t('milk.leaveBody')}</p>
      </ConfirmDialog>
    </div>
  );
}

function ZeroNoteDialog({ open, tags, onCancel, onSubmit }: { open: boolean; tags: string[]; onCancel: () => void; onSubmit: (note: string) => void }) {
  const { t } = useTranslation();
  const [note, setNote] = useState('');
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => !o && onCancel()}
      title={t('milk.zeroTitle')}
      description={t('milk.zeroBody', { tags: tags.join(', ') })}
      footer={
        <Button disabled={!note.trim()} onClick={() => onSubmit(note.trim())}>
          {t('common.save')}
        </Button>
      }
    >
      <Textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} aria-label={t('milk.zeroTitle')} />
    </Dialog>
  );
}

function WithholdDialog({ open, onCancel, onSubmit }: { open: boolean; onCancel: () => void; onSubmit: (reason: WithheldReason, note: string) => void }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState<WithheldReason>('OTHER');
  const [note, setNote] = useState('');
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => !o && onCancel()}
      title={t('milk.withholdTitle')}
      footer={
        <Button disabled={reason === 'OTHER' && !note.trim()} onClick={() => onSubmit(reason, note.trim())}>
          {t('milk.withhold')}
        </Button>
      }
    >
      <div className="space-y-3">
        <Field label={t('milk.reason')}>
          <Select value={reason} onChange={(e) => setReason(e.target.value as WithheldReason)}>
            <option value="COLOSTRUM">{t('milk.withheldReason.COLOSTRUM')}</option>
            <option value="OTHER">{t('milk.withheldReason.OTHER')}</option>
          </Select>
        </Field>
        <Field label={t('common.note')}>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} />
        </Field>
      </div>
    </Dialog>
  );
}
